'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import SectionTitle from '../ui/SectionTitle';
import GlassCard from '../ui/GlassCard';
import { soinCategories } from '@/app/lib/soins';

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: 'easeOut' as const },
  }),
};

export default function Services() {
  return (
    <section id="services" className="py-24 bg-cream-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          <SectionTitle
            eyebrow="Nos Soins"
            title="Une invitation au bien-être"
            subtitle="Chaque soin est soigneusement choisi pour vous offrir un moment d'exception, adapté à vos besoins."
          />
        </motion.div>

        <div className="mt-16 flex flex-col gap-16">
          {soinCategories.map((category) => (
            <div key={category.slug}>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6 }}
                className="mb-8"
              >
                <h3 className="font-serif text-2xl md:text-3xl text-forest-dark mb-2">
                  {category.name}
                </h3>
                {category.description && (
                  <p className="font-sans text-sm text-forest/60 leading-relaxed max-w-2xl">
                    {category.description}
                  </p>
                )}
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.soins.map((soin, i) => (
                  <motion.div
                    key={soin.slug}
                    custom={i}
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-60px' }}
                  >
                    <GlassCard className="p-7 h-full flex flex-col gap-4 group">
                      <div>
                        <h4 className="font-serif text-xl text-forest-dark mb-2">
                          {soin.name}
                        </h4>
                        <p className="font-sans text-sm text-forest/65 leading-relaxed">
                          {soin.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 font-sans text-xs text-forest/50">
                        <span>{soin.duration}</span>
                        <span>·</span>
                        <span className="font-medium text-forest/70">{soin.price}</span>
                      </div>
                      <div className="mt-auto pt-2">
                        <Link
                          href={`/soins/${soin.slug}`}
                          className="font-sans text-xs text-forest-light hover:text-forest tracking-wide transition-colors inline-flex items-center gap-1 group/link"
                        >
                          Découvrir & réserver
                          <span className="group-hover/link:translate-x-0.5 transition-transform">
                            →
                          </span>
                        </Link>
                      </div>
                    </GlassCard>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

